import { useQuery } from "@tanstack/react-query";
import type { DateRange } from "@/hooks/useDateRange";
import { analyticsReservationsService } from "@/services/analytics-reservations-service";
import { useAnalyticsOverviewRange } from "@/components/pages/analytics/front-desk/hooks/useAnalyticsOverviewRange";

export const reservationSummaryKeys = {
  all: ["analytics-reservations-summary"] as const,
  summary: (hotelId: string, from: string, to: string) =>
    [...reservationSummaryKeys.all, hotelId, from, to] as const,
};

/**
 * Stats for the Reservations summary block, backed by the real
 * GET /analytics/reservations/summary endpoint (active / canceled counts,
 * average length of stay, and upsell revenue by currency).
 */
export const useReservationSummaryStats = (dateRange: DateRange) => {
  const { hotelId, from, to, isReady } = useAnalyticsOverviewRange(dateRange);

  const query = useQuery({
    queryKey: reservationSummaryKeys.summary(hotelId, from, to),
    queryFn: () =>
      analyticsReservationsService.getSummary({ hotelId, from, to }),
    enabled: isReady,
    staleTime: 5 * 60 * 1000,
  });

  const data = query.data;

  return {
    // Primary currency total (net upsell in minor units); null when absent.
    upSell: data?.upsell.byCurrency[0] ?? null,
    activeReservations: data?.activeReservations ?? null,
    canceledReservations: data?.canceledReservations ?? null,
    avgLengthOfStayNights: data?.avgLengthOfStayNights ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
